import { Position } from '../../../Geo'
import { DeviceTypes } from '../../DeviceTypes'
import { NWMonitoringPayloadAttributes } from '../../MetaTypes'
import { Networks } from '../../NetworkTypes'
import {
  Bearings,
  IOTV_LCD_DeviceMessage,
  IoTV_LCD_DeviceRecord,
  IOTV_LCD_RX_Data,
  LinkType,
  MinimalBusinessObject,
  MinimalDeviceObject,
} from '../IOTV_LCD_MessageType'

/**
 * @link https://hal.science/hal-03210318/document
 */
const getESPCalculation = (rssi: number, snr: number): number | undefined => {
  let latter = 10 * Math.log10(1 + Math.pow(10, 0.1 * snr))
  return rssi + snr - latter

  // calculation through excel
  // let espHalf = Math.pow(rssi / 10, 10) / (1 + Math.pow(snr / 10, 10));
  // return 10 * Math.log10(Math.pow(rssi / 10, 10) - espHalf);
}

type FlatContextType = {
  dailyFCntSkip: number | undefined
  dailyMessageCount: number | undefined
  yesterdaysMessageCntTotal: number | undefined
  yesterdaysFCntJumpTotal: number | undefined
  entityId: string | undefined
  entityType: string | undefined
  entityName: string | undefined
  groupId: string | undefined
  groupName: string | undefined
  customerId: string | undefined
  customerName: string | undefined
  propertyId: string | undefined
  propertyName: string | undefined
  gatewayToDeviceBearing: number | undefined
  deviceBearing: number | undefined
  deviceToGatewayAngle: number | undefined
}

export type FlatLCDDeviceMessage = Omit<
  IOTV_LCD_DeviceMessage,
  | 'primaryGatewayRXData'
  | 'secondaryGatewayRXData'
  | 'nmMonitoringAttributes'
  | 'resolvedDevice'
  | 'resolvedEntity'
  | 'resolvedGroup'
  | 'resolvedCustomer'
  | 'resolvedProperty'
  | 'bearings'
> &
  Omit<IOTV_LCD_RX_Data, 'gatewayLocation'> &
  Partial<IOTV_LCD_RX_Data['gatewayLocation']> &
  //   & IOTV_LCD_DeviceMessage[ 'secondaryGatewayRXData' ]
  Omit<IOTV_LCD_DeviceMessage['nmMonitoringAttributes'], 'deviceLocation'> & {
    esp: number | undefined
    size: number | undefined
    otherGatewayEuis: string | undefined
    deviceLat: number | undefined
    deviceLng: number | undefined
    deviceDOP: number | undefined
    gatewayLat: number | undefined
    gatewayLng: number | undefined
    gatewayDOP: number | undefined
  } & FlatContextType

export const getFlatDeviceMessage = (deviceMessage: IOTV_LCD_DeviceMessage): FlatLCDDeviceMessage => {
  const {
    primaryGatewayRXData,
    secondaryGatewayRXData,
    nmMonitoringAttributes,
    deviceLocation,
    resolvedCustomer,
    resolvedDevice,
    resolvedEntity,
    resolvedGroup,
    resolvedProperty,
    bearings,
    ...deviceOthers
  } = deviceMessage
  const { gatewayLocation, ...gatewayOthers } = primaryGatewayRXData ?? ({} as IOTV_LCD_RX_Data)
  const { lat: gatewayLat, lng: gatewayLng, DOP: gatewayDOP } = gatewayLocation ?? {}
  const { position, ...nwMontioringAttributesOthers } = nmMonitoringAttributes ?? {}
  const { lat: deviceLat, lng: deviceLng, DOP: deviceDOP } = deviceLocation ?? position ?? {}
  const { id: entityId, type: entityType, name: entityName } = resolvedEntity ?? {}
  const { id: groupId, name: groupName } = resolvedGroup ?? {}
  const { dailyFCntSkip = 0, dailyMessageCount = 0, yesterdaysFCntJumpTotal = 0, yesterdaysMessageCntTotal = 0 } = resolvedDevice ?? {}
  const { id: customerId, name: customerName } = resolvedCustomer ?? {}
  const { id: propertyId, name: propertyName } = resolvedProperty ?? {}
  const { gatewayToDeviceBearing = undefined, deviceBearing = undefined, deviceToGatewayAngle = undefined } = bearings ?? {}
  const context = {
    entityId,
    entityType,
    entityName,
    groupId,
    groupName,
    customerId,
    dailyFCntSkip,
    dailyMessageCount,
    yesterdaysFCntJumpTotal,
    yesterdaysMessageCntTotal,
    customerName,
    propertyId,
    propertyName,
    gatewayToDeviceBearing,
    deviceBearing,
    deviceToGatewayAngle,
  }

  let otherGatewayEuis: string | undefined = undefined
  if ((secondaryGatewayRXData?.length ?? 0) > 0 && secondaryGatewayRXData.every((x) => !!x)) {
    otherGatewayEuis = secondaryGatewayRXData.map((gwData) => gwData.gatewayEui).join(',')
  }

  let size = deviceOthers.payloadStr?.length

  let esp: number | undefined = undefined
  if (gatewayOthers.rssi && gatewayOthers.snr) {
    esp = getESPCalculation(gatewayOthers.rssi, gatewayOthers.snr)
  }

  const flatRecord: FlatLCDDeviceMessage = {
    ...deviceOthers,
    ...gatewayOthers,
    ...nwMontioringAttributesOthers,
    ...context,
    esp,
    size,
    otherGatewayEuis,
    deviceLat,
    deviceLng,
    deviceDOP,
    gatewayLat,
    gatewayLng,
    gatewayDOP,
  }
  return flatRecord
}

type GatewayOthersExtractor = {
  gatewayEui: string
  gatewayIdName: string | undefined
  antenna: number | undefined
  channel: number | undefined
  channel_rssi: number | undefined
  rfChain: number | undefined
  rssi: number | undefined
  snr: number | undefined
}

export const hydrateFlatDeviceMessage = (flatMessage: FlatLCDDeviceMessage): IOTV_LCD_DeviceMessage => {
  const {
    adr,
    app_id,
    antenna,
    bw,
    channel,
    channel_rssi,
    codeRate,
    consumedAirtimeSecs,
    correlation_ids,
    customerId,
    customerName,
    deviceEui,
    deviceId,
    deviceName,
    dr,
    entityId,
    entityName,
    entityType,
    propertyId,
    propertyName,
    esp,
    frequency,
    gatewayEui,
    gatewayIdName,
    groupId,
    groupName,
    joinEui,
    linkType,
    modulation,
    nwID,
    otherGatewayEuis,
    payloadStr,
    port,
    power,
    rfChain,
    rssi,
    sf,
    size,
    snr,
    timestamp,
    uplinkId,
    DOP,
    age,
    battery,
    batteryCharging,
    batteryErr,
    deviceLocation,
    deviceType,
    downlinkAck,
    elevation,
    fCnt,
    fCntInc,
    lat,
    linkCheckErr,
    lng,
    nasSourceIp,
    reboot,
    dailyFCntSkip = 0,
    dailyMessageCount = 0,
    yesterdaysFCntJumpTotal,
    yesterdaysMessageCntTotal,
    deviceLat,
    deviceLng,
    deviceDOP,
    gatewayLat,
    gatewayLng,
    gatewayDOP,
    gatewayToDeviceBearing,
    deviceBearing,
    deviceToGatewayAngle,
  } = flatMessage

  const position: Position | undefined = deviceLat && deviceLng ? { lat: deviceLat, lng: deviceLng, DOP: deviceDOP, elevation, age } : undefined

  const nmMonitoringAttributes: NWMonitoringPayloadAttributes = {
    battery,
    batteryCharging,
    batteryErr,
    reboot,
    downlinkAck,
    linkCheckErr,
    position,
  }

  const gatewayLocation: Position | undefined = gatewayLat && gatewayLng ? { lat: gatewayLat, lng: gatewayLng, DOP: gatewayDOP } : undefined

  const primaryGatewayRXData: IOTV_LCD_RX_Data = {
    antenna,
    channel,
    channel_rssi,
    gatewayEui,
    gatewayIdName,
    gatewayLocation,
    rssi,
    rfChain,
    snr,
    esp,
  }

  /* 2025 04 RSE removed WYC added predicate that dailyFCntSkip and dailyMessageCount must be defined as that is clearly not what resolvedDevice should be. */
  const resolvedDevice: IoTV_LCD_DeviceRecord | undefined = {
    id: deviceEui,
    deviceId,
    appID: app_id,
    deviceType: deviceType ?? DeviceTypes.GenericSensor,
    eui: deviceEui,
    networkKey: nwID,
    type: 'Device',
    dailyFCntSkip,
    dailyMessageCount,
    yesterdaysFCntJumpTotal,
    yesterdaysMessageCntTotal,
  }
  const resolvedEntity: MinimalBusinessObject | undefined =
    entityId && entityType && entityName ? { id: entityId, type: entityType, name: entityName } : undefined
  const resolvedGroup: MinimalBusinessObject | undefined = groupId && groupName ? { id: groupId, type: 'Group', name: groupName } : undefined
  const resolvedProperty: MinimalBusinessObject | undefined = propertyId && propertyName ? { id: propertyId, type: 'Property', name: propertyId } : undefined
  const resolvedCustomer: MinimalBusinessObject | undefined = customerId && customerName ? { id: customerId, type: 'Customer', name: customerName } : undefined
  const bearings: Bearings = { gatewayToDeviceBearing, deviceBearing, deviceToGatewayAngle }
  const deviceMessage: IOTV_LCD_DeviceMessage = {
    adr,
    app_id,
    bw,
    codeRate,
    consumedAirtimeSecs,
    correlation_ids,
    deviceEui,
    deviceId,
    deviceName,
    dr,
    frequency,
    joinEui,
    linkType,
    modulation,
    nmMonitoringAttributes,
    nwID,
    payloadStr,
    port,
    power,
    secondaryGatewayRXData: [] /** Provisonally we will decide to join this with Gateway data table if requred */,
    sf,
    timestamp,
    uplinkId,
    deviceLocation,
    deviceType,
    fCnt,
    fCntInc,
    nasSourceIp,
    primaryGatewayRXData,
    resolvedDevice,
    resolvedCustomer,
    resolvedEntity,
    resolvedGroup,
    resolvedProperty,
    bearings,
  }

  return deviceMessage
}
